import { useEffect, useState, useMemo, useRef } from 'react';
import { Paperclip, X, Repeat, Check, ExternalLink, Banknote, Link as LinkIcon, Pencil, FileText, Upload, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import CategoryCombobox from './CategoryCombobox.jsx';
import Button from './Button.jsx';
import VatEditor from './VatEditor.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';
import { usePatchTransactionCategory } from '../hooks/useTransactions.js';
import { useCreateRecurrence, useRecurrences } from '../hooks/useRecurrences.js';
import { useDebts, useLinkTransactionToDebt, useUnlinkTransactionFromDebt } from '../hooks/useDebts.js';
import { usePatchTransactionRecurrence, usePatchTransactionIntercompany } from '../hooks/useTransactions.js';
import { useEntityScope } from '../hooks/useEntityScope.js';
import { normalizeLabel } from '../lib/normalize.js';
import { useTransactionAttachments, useUploadTransactionAttachment, attachmentFileUrl } from '../hooks/useTransactionAttachments.js';

const FREQ_LABELS = {
  weekly:    'Hebdomadaire',
  monthly:   'Mensuelle',
  quarterly: 'Trimestrielle',
  yearly:    'Annuelle',
  custom:    'Personnalisée',
};

const STATUS_LABELS = {
  pending:   { label: 'En attente',  badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  confirmed: { label: 'Confirmée',   badge: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  punctual:  { label: 'Ponctuelle',  badge: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200' },
  dismissed: { label: 'Rejetée',     badge: 'bg-slate-50 text-slate-500 ring-1 ring-slate-200' },
};

/**
 * Helper : ISO date (YYYY-MM-DD) à N mois dans le futur.
 */
function addMonthsISO(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function Field({ label, value, mono = false }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 text-sm">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className={`text-slate-800 text-right break-words ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
    </div>
  );
}

export default function TransactionDetailSlideOver({ transaction, open, onClose }) {
  const tx = transaction;
  const [newCat, setNewCat] = useState('');
  const patch = usePatchTransactionCategory();

  // État du formulaire "Marquer comme récurrence"
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [recFrequency, setRecFrequency] = useState('monthly');
  const [recEndMode, setRecEndMode] = useState('none');
  const [recEndDate, setRecEndDate] = useState('');
  const createRec = useCreateRecurrence();

  // État "Lier à une dette"
  const [selectedDebtId, setSelectedDebtId] = useState('');
  const { data: debtsData } = useDebts();
  const linkTx = useLinkTransactionToDebt();
  const unlinkTx = useUnlinkTransactionFromDebt();

  // État « Lier à une récurrence » (lien explicite, distinct de la détection label_pattern)
  const [selectedRecId, setSelectedRecId] = useState('');
  const patchTxRec = usePatchTransactionRecurrence();

  // Justificatifs Qonto — chargés à l'ouverture, affichés en flux (rien n'est stocké côté Treasury)
  const attachmentsQuery = useTransactionAttachments(tx?.id, open);
  const uploadAttachment = useUploadTransactionAttachment(tx?.id);
  const fileInputRef = useRef(null);

  // Glisser-déposer d'un justificatif sur tout le panneau.
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);
  const [clientError, setClientError] = useState('');
  const vatSectionRef = useRef(null);

  // Proposition de TVA lue par le serveur sur le dernier justificatif envoyé —
  // affichée dans VatEditor tant qu'elle n'a pas été enregistrée/ignorée.
  const [vatSuggestion, setVatSuggestion] = useState(null);

  // Détecte si la transaction est déjà liée à une récurrence (match label_pattern + amount)
  const { data: allRecurrences } = useRecurrences();
  const existingRecurrence = useMemo(() => {
    if (!tx) return null;
    const list = Array.isArray(allRecurrences) ? allRecurrences : allRecurrences?.recurrences ?? allRecurrences?.data ?? [];
    const normLabel = normalizeLabel(tx.label);
    if (!normLabel) return null;
    const absAmount = Math.abs(tx.amount_cents ?? 0);
    return list.find((r) => {
      if (r.status === 'dismissed') return false; // rejetée = comme inexistante
      if (r.label_pattern !== normLabel) return false;
      // Tolérance ±10 % sur le montant (correspond au groupement recurrences)
      const recAmount = Math.abs(r.amount_cents ?? 0);
      if (recAmount === 0) return false;
      const delta = Math.abs(recAmount - absAmount) / recAmount;
      return delta <= 0.10;
    }) ?? null;
  }, [tx, allRecurrences]);

  // ESC to close
  useEffect(() => {
    function handler(e) {
      if (e.key === 'Escape') onClose?.();
    }
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset form state when switching transaction.
  // Pré-remplit la catégorie avec celle actuellement assignée : plus rapide
  // pour affiner (changer la sous-catégorie) sans devoir tout retaper.
  useEffect(() => {
    setNewCat(tx?.category_id ?? '');
    setRecurrenceOpen(false);
    setRecFrequency('monthly');
    setRecEndMode('none');
    setRecEndDate('');
    setSelectedDebtId('');
    patch.reset?.();
    createRec.reset?.();
    linkTx.reset?.();
    unlinkTx.reset?.();
    uploadAttachment.reset?.();
    setClientError('');
    setVatSuggestion(null);
  }, [tx?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ferme aussi la suggestion en cours quand le panneau se ferme.
  useEffect(() => {
    if (!open) setVatSuggestion(null);
  }, [open]);

  // Fait défiler jusqu'à l'éditeur de TVA dès qu'une nouvelle proposition arrive.
  useEffect(() => {
    if (vatSuggestion) {
      vatSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [vatSuggestion]);

  function computeRecEndDate() {
    switch (recEndMode) {
      case 'none':   return null;
      case '3m':     return addMonthsISO(3);
      case '6m':     return addMonthsISO(6);
      case '12m':    return addMonthsISO(12);
      case 'custom': return recEndDate || null;
      default:       return null;
    }
  }

  async function handleCreateRecurrence() {
    if (!tx) return;
    const startDate = (tx.operation_date ?? tx.settled_at ?? new Date().toISOString()).slice(0, 10);
    // On envoie le `tx.label` brut : le backend normalise via normalizeLabel() —
    // c'est la même normalisation qu'on utilise côté frontend pour detecter
    // l'existingRecurrence, donc le match sera garanti après refetch.
    await createRec.mutateAsync({
      label_pattern: tx.label ?? tx.clean_counterparty_name ?? 'Récurrence',
      label_sample:  tx.clean_counterparty_name ?? tx.label,
      amount_cents:  Math.abs(tx.amount_cents ?? 0),
      frequency:     recFrequency,
      start_date:    startDate,
      end_date:      computeRecEndDate(),
      category_id:   tx.category_id ?? null,
      status:        'confirmed',
    });
    setRecurrenceOpen(false);
  }

  if (!tx) return null;

  const isDebit = tx.side === 'debit' || tx.amount_cents < 0;
  const amount = Math.abs(tx.amount_cents ?? 0);
  const displayName =
    tx.clean_counterparty_name ?? tx.counterparty_name ?? tx.label ?? '—';

  const attachments = attachmentsQuery.data?.items ?? [];

  // Le dépôt/l'upload ne sont proposés que quand la liste des justificatifs a pu
  // être chargée — même condition que celle qui affiche déjà le bouton "Ajouter".
  const canUploadAttachment = !attachmentsQuery.isLoading && !attachmentsQuery.isError;

  const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
  const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20 Mo

  function validateAttachmentFile(file) {
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      return 'Format non supporté (JPEG, PNG ou PDF uniquement).';
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      return 'Fichier trop volumineux (20 Mo max).';
    }
    if (attachments.length >= 5) {
      return 'Maximum 5 justificatifs (limite Qonto).';
    }
    return null;
  }

  // Point d'entrée commun au bouton "Ajouter un justificatif" et au glisser-déposer.
  async function uploadFile(file, { warning } = {}) {
    if (!canUploadAttachment || uploadAttachment.isPending) return; // pas d'envoi simultané
    const validationError = validateAttachmentFile(file);
    if (validationError) {
      setClientError(validationError);
      return;
    }
    setClientError(warning ?? '');
    try {
      const response = await uploadAttachment.mutateAsync(file);
      // Qonto traite l'upload en asynchrone → re-poll après un court délai.
      setTimeout(() => attachmentsQuery.refetch(), 2500);
      if (response?.extracted) {
        setVatSuggestion(response.extracted);
      }
    } catch {
      // erreur exposée via uploadAttachment.isError
    }
  }

  async function handleAttachmentSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file) return;
    await uploadFile(file);
  }

  function dragEventHasFiles(e) {
    return Array.from(e.dataTransfer?.types ?? []).includes('Files');
  }

  function handleDragEnter(e) {
    if (!canUploadAttachment || !dragEventHasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragActive(true);
  }

  function handleDragOver(e) {
    if (!canUploadAttachment || !dragEventHasFiles(e)) return;
    e.preventDefault();
  }

  function handleDragLeave(e) {
    if (!canUploadAttachment || !dragEventHasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragActive(false);
  }

  async function handleDrop(e) {
    if (!canUploadAttachment || !dragEventHasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    const [file] = files;
    if (files.length > 1) {
      await uploadFile(file, { warning: 'Un seul justificatif à la fois — seul le premier fichier a été envoyé.' });
    } else {
      await uploadFile(file);
    }
  }

  async function handleSaveCategory() {
    if (!newCat) return;
    await patch.mutateAsync({ id: tx.id, category_id: newCat });
    setNewCat('');
    onClose?.();
  }

  return (
    <>
      {/* Overlay */}
      <div
        className={`
          fixed inset-0 z-40 transition-opacity duration-200
          ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          bg-black/20 lg:bg-black/0
          lg:right-[560px] lg:left-0 xl:right-[640px]
        `}
        aria-hidden
        onClick={onClose}
      />

      {/* Panel — bottom sheet on mobile, right drawer on desktop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Détail de la transaction"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          fixed z-50 bg-white shadow-2xl
          transition-transform duration-200 ease-out

          inset-x-0 bottom-0 rounded-t-2xl max-h-[92dvh] overflow-y-auto
          ${open ? 'translate-y-0' : 'translate-y-full'}

          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh lg:w-[560px] xl:w-[640px] lg:rounded-none lg:max-h-none lg:overflow-y-auto
          ${open ? 'lg:translate-x-0' : 'lg:translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-900">Détail de la transaction</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-4 space-y-4">
          {/* Header: logo + nom + montant + TVA compacte (accolée) */}
          <div className="pb-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              {tx.logo_url ? (
                <img
                  src={tx.logo_url}
                  alt=""
                  className="w-12 h-12 rounded-full shrink-0 bg-slate-100 object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-semibold shrink-0">
                  {displayName.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500">{formatDate(tx.settled_at ?? tx.operation_date)}</p>
              </div>
              <div className={`text-lg font-semibold tabular-nums ${isDebit ? 'text-slate-900' : 'text-green-600'}`}>
                {isDebit ? '-' : '+'}{formatCurrency(amount)}
              </div>
            </div>

            {/* TVA inline — édition du taux (unique ou multi-taux). */}
            <div ref={vatSectionRef}>
              <VatEditor
                key={tx.id}
                transaction={tx}
                suggestion={vatSuggestion}
                onDismissSuggestion={() => setVatSuggestion(null)}
              />
              {uploadAttachment.isPending && (
                <p className="mt-1 pl-[60px] text-[11px] text-slate-400 flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" /> Envoi et lecture du justificatif…
                </p>
              )}
            </div>
          </div>

          {/* Catégorisation */}
          <div>
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Catégorisation</p>
            <Field label="Catégorie actuelle" value={tx.category_name ?? '— (non classifiée)'} />
            <Field label="Source" value={tx.category_source} />
            <Field label="Catégorie Qonto" value={tx.qonto_cashflow_category} />
            <Field label="Sous-catégorie Qonto" value={tx.qonto_cashflow_subcategory} />
            <Field label="Type d'opération Qonto" value={tx.qonto_category} />

            <div className="mt-3 flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1">Changer la catégorie</label>
                <CategoryCombobox
                  value={newCat}
                  onChange={(v) => setNewCat(v ?? '')}
                  placeholder="Catégorie…"
                />
              </div>
              <Button
                onClick={handleSaveCategory}
                disabled={!newCat || newCat === tx.category_id || patch.isPending}
                loading={patch.isPending}
              >
                Enregistrer
              </Button>
            </div>
            {patch.error && (
              <p className="text-xs text-red-600 mt-1">{patch.error.message}</p>
            )}
          </div>

          {/* Statut + devise (compact — le montant est dans le header) */}
          {(tx.status || tx.currency) && (
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {tx.status && <span>Statut : <span className="text-slate-700">{tx.status}</span></span>}
              {tx.status && tx.currency && <span className="text-slate-300">·</span>}
              {tx.currency && <span>Devise : <span className="text-slate-700">{tx.currency}</span></span>}
            </div>
          )}

          {/* Moyen de paiement */}
          {(tx.operation_type || tx.card_last_digits || tx.initiator_name) && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Moyen de paiement</p>
              <Field label="Type" value={tx.operation_type} />
              {tx.card_last_digits && <Field label="Carte" value={`•••• ${tx.card_last_digits}`} />}
              <Field label="Payé par" value={tx.initiator_name} />
            </div>
          )}

          {/* Métadonnées */}
          {(tx.note || tx.reference) && (
            <div className="pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Métadonnées</p>
              <Field label="Référence" value={tx.reference} mono />
              {tx.note && (
                <div className="py-1.5">
                  <p className="text-sm text-slate-500 mb-1">Note</p>
                  <p className="text-sm text-slate-700 italic bg-slate-50 rounded px-3 py-2">« {tx.note} »</p>
                </div>
              )}
            </div>
          )}

          {/* Pièces jointes — affichées en flux depuis Qonto (aucun stockage côté Treasury) */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1.5">
              <Paperclip size={14} /> Pièces jointes
            </p>

            {attachmentsQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 size={14} className="animate-spin" /> Chargement…
              </div>
            )}

            {attachmentsQuery.isError && (
              <p className="text-xs text-rose-600">Impossible de charger les justificatifs Qonto.</p>
            )}

            {!attachmentsQuery.isLoading && !attachmentsQuery.isError && (
              <>
                {attachments.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucun justificatif attaché côté Qonto.</p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {attachments.map((a) => {
                      const url = attachmentFileUrl(tx.id, a.id);
                      const isImage = a.file_content_type?.startsWith('image/');
                      return (
                        <li key={a.id}>
                          <a href={url} target="_blank" rel="noreferrer" title={a.file_name} className="block">
                            {isImage ? (
                              <img
                                src={url}
                                alt={a.file_name}
                                loading="lazy"
                                className="h-20 w-20 rounded-lg border border-slate-200 object-cover hover:opacity-90"
                              />
                            ) : (
                              <span className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100">
                                <FileText size={20} />
                                <span className="w-full truncate px-1 text-center text-[10px] leading-tight">
                                  {a.file_name?.split('.').pop()?.toUpperCase() || 'FICHIER'}
                                </span>
                              </span>
                            )}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Upload */}
                <div className="mt-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    className="hidden"
                    onChange={handleAttachmentSelected}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadAttachment.isPending || attachments.length >= 5}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {uploadAttachment.isPending ? (
                      <><Loader2 size={14} className="animate-spin" /> Envoi…</>
                    ) : (
                      <><Upload size={14} /> Ajouter un justificatif</>
                    )}
                  </button>

                  {attachments.length >= 5 && (
                    <p className="mt-1 text-xs text-slate-400">Maximum 5 justificatifs (limite Qonto).</p>
                  )}
                  {uploadAttachment.isPending && (
                    <p className="mt-1 text-xs text-slate-500 flex items-center gap-1.5">
                      <Loader2 size={12} className="animate-spin" /> Envoi et lecture du justificatif…
                    </p>
                  )}
                  {uploadAttachment.isSuccess && !uploadAttachment.isPending && (
                    <p className="mt-1 text-xs text-emerald-600">
                      Justificatif envoyé — traitement en cours côté Qonto, il apparaîtra dans un instant.
                    </p>
                  )}
                  {uploadAttachment.isError && (
                    <p className="mt-1 text-xs text-rose-600">
                      {uploadAttachment.error?.message ?? "Échec de l'envoi."}
                    </p>
                  )}
                  {clientError && (
                    <p role="alert" className="mt-1 text-xs text-rose-600">{clientError}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">JPEG, PNG ou PDF · 20 Mo max.</p>
                </div>
              </>
            )}
          </div>

          {/* Récurrence */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Récurrence</p>

            {existingRecurrence && !recurrenceOpen ? (
              // Récap d'une récurrence existante (détection par label_pattern + amount ±10%)
              <div className="rounded-lg bg-slate-50 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Repeat size={14} className="text-emerald-600 shrink-0" />
                  <span className="text-sm font-semibold text-slate-900 truncate">
                    {existingRecurrence.label_sample ?? existingRecurrence.label_pattern}
                  </span>
                  <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_LABELS[existingRecurrence.status]?.badge ?? ''}`}>
                    {STATUS_LABELS[existingRecurrence.status]?.label ?? existingRecurrence.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  <span>
                    <span className="text-slate-400">Fréquence :</span>{' '}
                    <span className="font-medium">{FREQ_LABELS[existingRecurrence.frequency] ?? existingRecurrence.frequency}</span>
                  </span>
                  <span className="text-slate-300">·</span>
                  <span>
                    <span className="text-slate-400">Montant :</span>{' '}
                    <span className="font-medium tabular-nums">{formatCurrency(Math.abs(existingRecurrence.amount_cents ?? 0))}</span>
                  </span>
                  <span className="text-slate-300">·</span>
                  <span>
                    <span className="text-slate-400">Fin :</span>{' '}
                    <span className="font-medium">{existingRecurrence.end_date ? formatDate(existingRecurrence.end_date) : 'Indéterminée'}</span>
                  </span>
                </div>
                <div className="flex gap-3 pt-1">
                  <Link
                    to="/previsionnels"
                    onClick={onClose}
                    className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 underline"
                  >
                    Gérer dans Prévisionnels <ExternalLink size={11} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setRecurrenceOpen(true)}
                    className="text-xs text-slate-500 hover:text-slate-800"
                  >
                    Remplacer
                  </button>
                </div>
              </div>
            ) : !recurrenceOpen ? (
              <button
                type="button"
                onClick={() => setRecurrenceOpen(true)}
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 px-3 py-2 rounded-lg w-full transition-colors"
              >
                <Repeat size={14} className="text-slate-500" />
                <span>Marquer comme récurrence</span>
              </button>
            ) : (
              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                {/* Fréquence */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Fréquence</label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: 'monthly',   label: 'Mensuelle' },
                      { id: 'quarterly', label: 'Trimestrielle' },
                      { id: 'yearly',    label: 'Annuelle' },
                      { id: 'custom',    label: 'Personnalisée' },
                    ].map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setRecFrequency(f.id)}
                        className={`text-xs px-2.5 py-1 rounded-full ring-1 transition-colors ${
                          recFrequency === f.id
                            ? 'bg-slate-800 text-white ring-slate-800'
                            : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-400'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Date de fin */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Date de fin</label>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { id: 'none',   label: 'Indéterminé' },
                      { id: '3m',     label: '3 mois' },
                      { id: '6m',     label: '6 mois' },
                      { id: '12m',    label: '12 mois' },
                      { id: 'custom', label: 'Date précise' },
                    ].map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setRecEndMode(p.id)}
                        className={`text-xs px-2.5 py-1 rounded-full ring-1 transition-colors ${
                          recEndMode === p.id
                            ? 'bg-slate-800 text-white ring-slate-800'
                            : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-400'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {recEndMode === 'custom' && (
                    <input
                      type="date"
                      value={recEndDate}
                      onChange={(e) => setRecEndDate(e.target.value)}
                      className="mt-2 text-xs rounded-lg border border-slate-300 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                    />
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={handleCreateRecurrence}
                    loading={createRec.isPending}
                    disabled={recEndMode === 'custom' && !recEndDate}
                    className="text-xs gap-1"
                  >
                    <Check size={13} /> Créer la récurrence
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setRecurrenceOpen(false)}
                    className="text-xs"
                  >
                    Annuler
                  </Button>
                </div>

                {createRec.isSuccess && (
                  <p className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1.5 rounded">
                    ✓ Récurrence créée — visible dans l'onglet Récurrences.
                  </p>
                )}
                {createRec.error && (
                  <p className="text-xs text-red-600">{createRec.error.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Lier à une dette */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Dette</p>
            {(() => {
              const allDebts = Array.isArray(debtsData) ? debtsData : debtsData?.debts ?? [];
              const activeDebts = allDebts.filter((d) => d.status === 'active');
              const linkedDebt = tx.debt_id ? allDebts.find((d) => d.id === tx.debt_id) : null;

              if (linkedDebt) {
                return (
                  <div className="rounded-lg bg-slate-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Banknote size={14} className="text-sky-600 shrink-0" />
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {linkedDebt.name}
                      </span>
                    </div>
                    <div className="flex gap-3 pt-1">
                      <Link
                        to={`/dettes/${linkedDebt.id}`}
                        onClick={onClose}
                        className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 underline"
                      >
                        Voir la dette <ExternalLink size={11} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => unlinkTx.mutate({ debtId: linkedDebt.id, transactionId: tx.id })}
                        disabled={unlinkTx.isPending}
                        className="text-xs text-slate-500 hover:text-red-600 transition-colors disabled:opacity-50"
                      >
                        Délier
                      </button>
                    </div>
                    {unlinkTx.error && (
                      <p className="text-xs text-red-600">{unlinkTx.error.message}</p>
                    )}
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-xs text-slate-500 mb-1">Associer à une dette</label>
                      <select
                        value={selectedDebtId}
                        onChange={(e) => setSelectedDebtId(e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500"
                        disabled={linkTx.isPending}
                      >
                        <option value="">— Choisir une dette —</option>
                        {activeDebts.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </div>
                    <Button
                      onClick={() => {
                        if (!selectedDebtId) return;
                        linkTx.mutate({ debtId: selectedDebtId, transactionId: tx.id });
                      }}
                      disabled={!selectedDebtId || linkTx.isPending}
                      loading={linkTx.isPending}
                      className="text-xs gap-1"
                    >
                      <LinkIcon size={12} /> Lier
                    </Button>
                  </div>
                  {linkTx.error && (
                    <p className="text-xs text-red-600">{linkTx.error.message}</p>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Lier à une récurrence (lien explicite par debt_id → recurrence_id) */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Lien récurrence</p>
            {(() => {
              const allRecs = Array.isArray(allRecurrences) ? allRecurrences : allRecurrences?.recurrences ?? allRecurrences?.data ?? [];
              const linkedRec = tx.recurrence_id ? allRecs.find((r) => r.id === tx.recurrence_id) : null;
              const confirmedRecs = allRecs.filter((r) => r.status === 'confirmed' || r.status === 'punctual');

              if (linkedRec) {
                const label = linkedRec.label_sample ?? linkedRec.label_pattern ?? 'Récurrence';
                return (
                  <div className="rounded-lg bg-slate-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Repeat size={14} className="text-sky-600 shrink-0" />
                      <span className="text-sm font-semibold text-slate-900 truncate">{label}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => patchTxRec.mutate({ id: tx.id, recurrence_id: null })}
                      disabled={patchTxRec.isPending}
                      className="text-xs text-slate-500 hover:text-red-600 transition-colors disabled:opacity-50"
                    >
                      Délier
                    </button>
                  </div>
                );
              }

              return (
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">Associer à une récurrence confirmée</label>
                    <select
                      value={selectedRecId}
                      onChange={(e) => setSelectedRecId(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500"
                      disabled={patchTxRec.isPending}
                    >
                      <option value="">— Choisir une récurrence —</option>
                      {confirmedRecs.map((r) => (
                        <option key={r.id} value={r.id}>
                          {(r.label_sample ?? r.label_pattern)} — {formatCurrency(Math.abs(r.amount_cents ?? 0))}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={() => {
                      if (!selectedRecId) return;
                      patchTxRec.mutate({ id: tx.id, recurrence_id: selectedRecId });
                    }}
                    disabled={!selectedRecId || patchTxRec.isPending}
                    loading={patchTxRec.isPending}
                    className="text-xs gap-1"
                  >
                    <LinkIcon size={12} /> Lier
                  </Button>
                </div>
              );
            })()}
          </div>

          {/* Contrepartie intra-groupe : exclue de la vue groupe, rapprochée aux refacturations projetées */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Flux intra-groupe</p>
            <IntercompanySection tx={tx} />
          </div>

          {/* IDs techniques */}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Identifiants</p>
            <Field label="ID Qonto" value={tx.qonto_id} mono />
            <Field label="ID interne" value={tx.id} mono />
          </div>
        </div>
      </div>

      {/* Voile de dépôt — frère du panneau et non enfant : le panneau défile et porte
          une transformation, un voile enfant suivrait le contenu. Même géométrie que
          le panneau ; ne capte aucun événement (le drop est géré par le panneau). */}
      {dragActive && (
        <div className="pointer-events-none fixed z-[60] inset-x-0 bottom-0 h-[92dvh] p-2 lg:inset-x-auto lg:right-0 lg:top-0 lg:h-dvh lg:w-[560px] xl:w-[640px]">
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-sky-400 bg-sky-50/95">
            <p className="text-sm font-semibold text-sky-700">Déposez le justificatif</p>
            <p className="text-xs text-sky-600">JPEG, PNG ou PDF · 20 Mo max</p>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Marquage de la contrepartie intra-groupe (autre société du groupe).
 * Auto à la synchro, corrigeable ici ; un choix manuel n'est jamais écrasé.
 */
function IntercompanySection({ tx }) {
  const { memberships, activeEntityId } = useEntityScope();
  const patchIc = usePatchTransactionIntercompany();
  const [selected, setSelected] = useState('');
  const candidates = memberships.filter((m) => m.entity_id !== activeEntityId && m.include_in_group !== false);

  if (tx.intercompany_entity_id) {
    return (
      <div className="rounded-lg bg-violet-50 p-3 space-y-2">
        <p className="text-sm text-slate-800">
          Contrepartie : <span className="font-semibold">{tx.intercompany_entity_name ?? tx.intercompany_entity_slug ?? 'société du groupe'}</span>
          <span className="ml-2 text-xs text-slate-500">{tx.intercompany_source === 'manual' ? 'marqué à la main' : 'détecté automatiquement'}</span>
        </p>
        <p className="text-xs text-slate-500">Exclu de la Vue groupe ; compte comme paiement des refacturations projetées du mois.</p>
        <button
          type="button"
          onClick={() => patchIc.mutate({ id: tx.id, intercompany_entity_id: null })}
          disabled={patchIc.isPending}
          className="text-xs text-slate-500 hover:text-red-600 transition-colors disabled:opacity-50"
        >
          Retirer le marquage
        </button>
        {patchIc.error && <p className="text-xs text-red-600">{patchIc.error.message}</p>}
      </div>
    );
  }

  if (candidates.length === 0) {
    return <p className="text-xs text-slate-400">Aucune autre société du groupe.</p>;
  }

  return (
    <div className="flex gap-2 items-end">
      <div className="flex-1">
        <label className="block text-xs text-slate-500 mb-1">Marquer comme flux avec</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500"
          disabled={patchIc.isPending}
        >
          <option value="">— Choisir une société —</option>
          {candidates.map((m) => (
            <option key={m.entity_id} value={m.entity_id}>{m.name}</option>
          ))}
        </select>
      </div>
      <Button
        onClick={() => { if (selected) patchIc.mutate({ id: tx.id, intercompany_entity_id: selected }); }}
        disabled={!selected || patchIc.isPending}
        loading={patchIc.isPending}
        className="text-xs gap-1"
      >
        <LinkIcon size={12} /> Marquer
      </Button>
      {patchIc.error && <p className="text-xs text-red-600">{patchIc.error.message}</p>}
    </div>
  );
}
